import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import PinLegendType from "@mapmycustomers/shared/enum/PinLegendType";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { Group, Territory } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import {
  ColorItemType,
  ShapeItemType,
  SimpleClassificationColorItem,
  SimpleClassificationShapeItem,
} from "@mapmycustomers/shared/types/map/PinLegend";

import { getFunnels, getFunnelStages } from "@app/store/deal";
import { getGroupsForEntity } from "@app/store/groups";
import { RootState } from "@app/store/rootReducer";
import { getTerritories } from "@app/store/territories";
import { fetchAllTerritories } from "@app/store/territories/actions";
import mmcColors from "@app/styles/_colors.module.scss";
import { isDefaultColorOption, isDefaultShapeOption } from "@app/util/pinLegend";

import BaseStyleEditor from "./components/BaseStyleEditor";
import ColorOptionItem from "./components/ColorOptionItem";
import OptionList from "./components/OptionList";
import ShapeOptionItem from "./components/ShapeOptionItem";
import BaseStyleEditorProps from "./types/BaseStyleEditorProps";
import getDefaultColors from "./utils/getDefaultColors";
import getDefaultShapes from "./utils/getDefaultShapes";
import useOptionGroups from "./utils/useOptionGroups";
import useOptions from "./utils/useOptions";

interface OwnProps extends BaseStyleEditorProps {}

interface Props extends OwnProps {
  funnels: Funnel[];
  groups: Group[];
  onFetchTerritories?: () => void;
  stages: Stage[];
  territories: Territory[];
}

const OptionStyleEditor: React.FC<Props> = ({
  colors,
  field,
  funnels,
  groups,
  onColorChange,
  onFetchTerritories,
  onShapeChange,
  shapes,
  stages,
  territories,
  type,
}) => {
  const intl = useIntl();

  const options = useOptions(field, groups, funnels, territories, stages);
  const optionGroups = useOptionGroups(field, funnels);

  useEffect(() => {
    // do not add "Unknown" option for Funnel field
    const addUnknown = !field.hasAnyFeature(FieldFeature.FUNNEL_FIELD, FieldFeature.STAGE_FIELD);

    if (type === PinLegendType.COLOR && colors && !colors.length) {
      // initialize when colors are not defined (when we're creating new custom pin legend)
      onColorChange?.(getDefaultColors(options, addUnknown));
    }
    if (type === PinLegendType.SHAPE && shapes && !shapes.length) {
      // we handle initializing shapes for Territories in a separate useEffect
      if (!field.hasFeature(FieldFeature.TERRITORY_FIELD)) {
        // initialize when shapes are not defined (when we're creating new custom pin legend)
        onShapeChange?.(getDefaultShapes(options, addUnknown));
      }
    }
  }, [colors, field, onColorChange, onShapeChange, options, shapes, type]);

  useEffect(() => {
    if (type === PinLegendType.SHAPE && field.hasFeature(FieldFeature.TERRITORY_FIELD)) {
      if (!options.length) {
        // if territories are not yet fetched, fetch them
        onFetchTerritories?.();
      } else if (!shapes?.length) {
        // otherwise, let's set default shapes
        onShapeChange?.(getDefaultShapes(options, true));
      }
    }
  }, [type, field, territories, options, shapes, onFetchTerritories, onShapeChange]);

  const handleReset = useCallback(() => {
    if (type === PinLegendType.COLOR) {
      onColorChange?.(
        colors?.map((color) => ({ ...color, color: mmcColors.gray as AnyColor })) ?? []
      );
    } else {
      onShapeChange?.(shapes?.map((shape) => ({ ...shape, shape: MarkerShape.SQUARE })) ?? []);
    }
  }, [colors, onColorChange, onShapeChange, shapes, type]);

  const resetIsDisabled = useMemo(
    () =>
      type === PinLegendType.COLOR
        ? !!colors?.every(({ color }) => color === (mmcColors.gray as AnyColor))
        : !!shapes?.every(({ shape }) => shape === MarkerShape.SQUARE),
    [colors, shapes, type]
  );

  const [wonStageIds, lostStageIds] = useMemo(() => {
    const wonStageIds = new Set<number>();
    const lostStageIds = new Set<number>();
    stages.forEach((stage) => {
      if (stage.type === DealStageType.WON) {
        wonStageIds.add(stage.id);
      } else if (stage.type === DealStageType.LOST) {
        lostStageIds.add(stage.id);
      }
    });
    return [wonStageIds, lostStageIds];
  }, [stages]);

  const needShowBulkUpdating = useCallback(
    (value: number) => wonStageIds.has(value) || lostStageIds.has(value),
    [wonStageIds, lostStageIds]
  );

  const handleBulkUpdate = useCallback(
    (item: SimpleClassificationColorItem | SimpleClassificationShapeItem) => {
      const searchingSet = wonStageIds.has(item.fieldValue as number) ? wonStageIds : lostStageIds;
      if (type === PinLegendType.COLOR) {
        onColorChange?.(
          (colors as SimpleClassificationColorItem[])?.map((color) =>
            searchingSet.has(color.fieldValue as number)
              ? { ...color, color: (item as ColorItemType).color }
              : color
          ) ?? []
        );
      } else {
        onShapeChange?.(
          (shapes as SimpleClassificationShapeItem[])?.map((shape) =>
            searchingSet.has(shape.fieldValue as number)
              ? { ...shape, shape: (item as ShapeItemType).shape }
              : shape
          ) ?? []
        );
      }
    },
    [colors, lostStageIds, onColorChange, onShapeChange, shapes, type, wonStageIds]
  );

  const getBulkUpdateText = useCallback(
    (item: SimpleClassificationColorItem | SimpleClassificationShapeItem) => {
      return wonStageIds.has(item.fieldValue as number)
        ? type === PinLegendType.COLOR
          ? intl.formatMessage({
              id: "map.colorization.createEditSidebar.styleEditor.bulkUpdate.won.color",
              defaultMessage: "Apply new color to “Closed Won” stages across all funnels?",
              description: "Text for bulk update button in the Style Editor",
            })
          : intl.formatMessage({
              id: "map.colorization.createEditSidebar.styleEditor.bulkUpdate.won.shape",
              defaultMessage: "Apply new shape to “Closed Won” stages across all funnels?",
              description: "Text for bulk update button in the Style Editor",
            })
        : type === PinLegendType.COLOR
        ? intl.formatMessage({
            id: "map.colorization.createEditSidebar.styleEditor.bulkUpdate.lost.color",
            defaultMessage: "Apply new color to “Closed Lost” stages across all funnels?",
            description: "Text for bulk update button in the Style Editor",
          })
        : intl.formatMessage({
            id: "map.colorization.createEditSidebar.styleEditor.bulkUpdate.lost.shape",
            defaultMessage: "Apply new shape to “Closed Lost” stages across all funnels?",
            description: "Text for bulk update button in the Style Editor",
          });
    },
    [intl, type, wonStageIds]
  );

  return (
    <BaseStyleEditor
      description={
        type === PinLegendType.COLOR
          ? intl.formatMessage({
              id: "map.colorization.createEditSidebar.styleEditor.optional.description.color",
              defaultMessage:
                "Each unique value has been assigned a color automatically. Click the color indicator to pick another color.",
              description:
                "Description of the Optional Style editor in Create Pin Legend drawer on the Map page",
            })
          : intl.formatMessage({
              id: "map.colorization.createEditSidebar.styleEditor.optional.description.shape",
              defaultMessage:
                "Each unique value has been assigned a shape automatically. Click the indicator to select another shape.",
              description:
                "Description of the Optional Style editor in Create Pin Legend drawer on the Map page",
            })
      }
      onReset={handleReset}
      pinLegendType={type}
      resetIsDisabled={resetIsDisabled}
    >
      {type === PinLegendType.COLOR ? (
        <OptionList<SimpleClassificationColorItem>
          getBulkUpdateText={getBulkUpdateText}
          isDefault={isDefaultColorOption}
          itemComponent={ColorOptionItem<SimpleClassificationColorItem>}
          needShowBulkUpdating={needShowBulkUpdating}
          onBulkUpdate={handleBulkUpdate}
          onChange={onColorChange}
          optionGroups={optionGroups}
          options={options}
          value={(colors as unknown as SimpleClassificationColorItem[]) ?? []}
        />
      ) : (
        <OptionList<SimpleClassificationShapeItem>
          getBulkUpdateText={getBulkUpdateText}
          isDefault={isDefaultShapeOption}
          itemComponent={ShapeOptionItem}
          needShowBulkUpdating={needShowBulkUpdating}
          onBulkUpdate={handleBulkUpdate}
          onChange={onShapeChange}
          optionGroups={optionGroups}
          options={options}
          value={(shapes as unknown as SimpleClassificationShapeItem[]) ?? []}
        />
      )}
    </BaseStyleEditor>
  );
};

const mapStateToProps = (state: RootState, { entityType }: OwnProps) => ({
  funnels: getFunnels(state),
  groups: getGroupsForEntity(state)(entityType),
  stages: Object.values(getFunnelStages(state))
    .flat()
    .sort((a, b) => a.displayOrder - b.displayOrder),
  territories: getTerritories(state),
});

const mapDispatchToProps = {
  onFetchTerritories: fetchAllTerritories.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(OptionStyleEditor);
